import { ActionIcon, Badge, Button, Card, Group, Menu, Pagination, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconDots, IconEdit, IconKey, IconLock, IconPlus, IconSearch, IconUserOff, IconUsers } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PRIVILEGED_ROLES, ROLE_LABELS, ROLES, USER_STATUSES, type Paginated, type Role, type UserDto } from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth, useCurrentUser } from '@/auth/AuthProvider';
import { AccountStatusBadge, RoleBadge } from '@/components/badges';
import { PageHeader } from '@/components/PageHeader';
import { SecretRevealModal } from '@/components/SecretReveal';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { departmentName, useDepartments } from '@/features/shared/lookups';
import { fromNow } from '@/lib/format';
import { notifySuccess } from '@/lib/notify';
import { SetPasswordModal } from './SetPasswordModal';
import { UserDrawer } from './UserDrawer';

const PAGE_SIZE = 25;

export function UsersPage() {
  const me = useCurrentUser();
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [role, setRole] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [department, setDepartment] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState<{ open: boolean; user: UserDto | null }>({ open: false, user: null });
  const [passwordFor, setPasswordFor] = useState<UserDto | null>(null);
  const [secret, setSecret] = useState<{ email: string; value: string; title: string } | null>(null);
  const departments = useDepartments();

  const list = useQuery({
    queryKey: ['users', 'list', { q, role, status, department, page }],
    queryFn: () => http.get<Paginated<UserDto>>('/users', { q, role, status, department, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const canEdit = can('Users', 'edit');
  const manageable = (u: UserDto) => me.role === 'super_admin' || !(PRIVILEGED_ROLES as readonly Role[]).includes(u.role);
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;
  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="Users"
        description="Staff and exporter sign-ins. Roles decide what each person can see and do."
        actions={
          canEdit && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setDrawer({ open: true, user: null })}>
              New user
            </Button>
          )
        }
      />
      <Card p={0}>
        <Group p="md" gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search name or email"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => resetPage(setSearch)(e.currentTarget.value)}
            w={{ base: '100%', sm: 260 }}
          />
          <Select placeholder="Any role" clearable data={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} value={role} onChange={resetPage(setRole)} w={200} />
          <Select
            placeholder="Any department"
            clearable
            searchable
            data={(departments.data ?? []).map((d) => ({ value: d.code, label: d.name }))}
            value={department}
            onChange={resetPage(setDepartment)}
            w={200}
          />
          <Select
            placeholder="Any status"
            clearable
            data={USER_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
            value={status}
            onChange={resetPage(setStatus)}
            w={150}
          />
        </Group>
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState icon={IconUsers} title="No users match" description="Try a different search or filter." />
        ) : (
          <div className="table-scroll">
            <Table miw={860}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Department / company</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Last sign-in</Table.Th>
                  <Table.Th w={48} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.items.map((u) => (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Group gap={6}>
                          <Text fw={600} fz="sm">
                            {u.fullName}
                          </Text>
                          {u.id === me.id && (
                            <Badge size="xs" variant="outline">
                              You
                            </Badge>
                          )}
                        </Group>
                        <Text fz="xs" c="dimmed">
                          {u.email}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <RoleBadge role={u.role} />
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{u.userType === 'exporter' ? (u.exporterName ?? '—') : departmentName(departments.data, u.department)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <AccountStatusBadge status={u.status} />
                        {u.locked && (
                          <Tooltip label="Locked after failed sign-ins; setting a password unlocks it">
                            <IconLock size={16} color="var(--mantine-color-orange-6)" />
                          </Tooltip>
                        )}
                        {!u.hasPassword && (
                          <Tooltip label="No password yet: this person cannot sign in until one is set">
                            <IconUserOff size={16} color="var(--mantine-color-red-6)" />
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="dimmed" className="nowrap">
                        {u.lastLoginAt ? fromNow(u.lastLoginAt) : 'Never'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {canEdit && manageable(u) && (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon aria-label={`Actions for ${u.fullName}`}>
                              <IconDots size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconEdit size={16} />} onClick={() => setDrawer({ open: true, user: u })}>
                              Edit details
                            </Menu.Item>
                            <Menu.Item leftSection={<IconKey size={16} />} onClick={() => setPasswordFor(u)}>
                              {u.hasPassword ? 'Reset password' : 'Set password'}
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
        {list.data && (
          <Group justify="space-between" p="md">
            <Text fz="sm" c="dimmed">
              {list.data.total} user{list.data.total === 1 ? '' : 's'}
            </Text>
            {pages > 1 && <Pagination total={pages} value={page} onChange={setPage} />}
          </Group>
        )}
      </Card>

      <UserDrawer
        opened={drawer.open}
        user={drawer.user}
        onClose={() => setDrawer({ open: false, user: null })}
        onCreated={(res) => {
          if (res.temporaryPassword) setSecret({ email: res.user.email, value: res.temporaryPassword, title: 'User created' });
          else notifySuccess(`${res.user.fullName} can now sign in.`);
        }}
      />
      <SetPasswordModal
        user={passwordFor}
        onClose={() => setPasswordFor(null)}
        onTemporary={(value) => passwordFor && setSecret({ email: passwordFor.email, value, title: 'Temporary password' })}
        onDone={() => notifySuccess('Password updated.')}
      />
      <SecretRevealModal opened={!!secret} onClose={() => setSecret(null)} title={secret?.title ?? ''} email={secret?.email ?? ''} secret={secret?.value ?? null} />
    </>
  );
}
